(() => {
  if (window.__pawlishProResultsMediaStats) return;
  window.__pawlishProResultsMediaStats = true;

  const ROOT_SELECTOR = '[data-pro-results-media]';
  const STAT_SELECTOR = '[data-prm-stat]';
  const NUMBER_SELECTOR = '[data-prm-percent]';
  const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
  const instances = new WeakMap();

  const clampPercent = (value) => Math.min(100, Math.max(0, Number.isFinite(value) ? value : 0));

  const targetFor = (stat) => clampPercent(Number.parseFloat(stat.dataset.prmTarget || '0'));

  const render = (stat, value) => {
    const safeValue = clampPercent(value);
    stat.style.setProperty('--prm-progress', safeValue.toFixed(2));

    const number = stat.querySelector(NUMBER_SELECTOR);
    if (number) number.textContent = `${Math.round(safeValue)}%`;
  };

  const renderTargets = (stats) => {
    stats.forEach((stat) => render(stat, targetFor(stat)));
  };

  const mount = (root) => {
    if (!root || instances.has(root)) return;

    const stats = [...root.querySelectorAll(STAT_SELECTOR)];
    if (!stats.length) return;

    const state = {
      observer: null,
      frameIds: new Set(),
      timeouts: new Set(),
      started: false,
      completed: 0,
    };

    const cleanupAnimationHandles = () => {
      state.frameIds.forEach((id) => cancelAnimationFrame(id));
      state.timeouts.forEach((id) => clearTimeout(id));
      state.frameIds.clear();
      state.timeouts.clear();
    };

    const finish = () => {
      cleanupAnimationHandles();
      renderTargets(stats);
      root.dataset.prmStatsAnimated = 'true';
    };

    const animateStat = (stat, index) => {
      const target = targetFor(stat);
      const delay = index * 110;
      const duration = 900;

      const timeoutId = window.setTimeout(() => {
        state.timeouts.delete(timeoutId);
        const start = performance.now();

        const tick = (now) => {
          const elapsed = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - elapsed, 3);
          render(stat, target * eased);

          if (elapsed < 1) {
            const frameId = requestAnimationFrame(tick);
            state.frameIds.add(frameId);
          } else {
            render(stat, target);
            state.completed += 1;
            if (state.completed === stats.length) {
              root.dataset.prmStatsAnimated = 'true';
              state.frameIds.clear();
              state.timeouts.clear();
            }
          }
        };

        const frameId = requestAnimationFrame(tick);
        state.frameIds.add(frameId);
      }, delay);

      state.timeouts.add(timeoutId);
    };

    const start = () => {
      if (state.started) return;
      state.started = true;
      state.observer?.disconnect();

      if (window.matchMedia(REDUCED_MOTION).matches) {
        finish();
        return;
      }

      stats.forEach((stat, index) => animateStat(stat, index));
    };

    const prepare = () => {
      if (root.dataset.prmStatsAnimated === 'true') {
        renderTargets(stats);
        return false;
      }

      stats.forEach((stat) => render(stat, 0));
      return true;
    };

    instances.set(root, {
      destroy() {
        state.observer?.disconnect();
        cleanupAnimationHandles();
        instances.delete(root);
      },
    });

    if (!prepare()) return;

    if (!('IntersectionObserver' in window)) {
      start();
      return;
    }

    state.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) start();
      },
      {
        threshold: 0.22,
        rootMargin: '0px 0px -10% 0px',
      }
    );

    state.observer.observe(root);
  };

  const unmount = (root) => {
    instances.get(root)?.destroy();
  };

  const mountAll = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) mount(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(mount);
  };

  const init = () => mountAll(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => mountAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches?.(ROOT_SELECTOR)) unmount(event.target);
    event.target.querySelectorAll?.(ROOT_SELECTOR).forEach(unmount);
  });
})();
